import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import { Loader2 } from 'lucide-react'

const initialOf = (name: string) => name.trim().charAt(0).toUpperCase()

interface Worker {
  id: string
  name: string
  job_title: string | null
  avatar_url: string | null
  assigned: boolean
}

interface AssignWorkersModalProps {
  open: boolean
  onClose: () => void
  providerLabel: string
  workers: Worker[]
  provider: string
  integrationId?: string
  isNotion?: boolean
  saving: boolean
  onSavingChange: (saving: boolean) => void
}

const AssignWorkersModal = ({
  open,
  onClose,
  providerLabel,
  workers,
  provider,
  integrationId,
  isNotion = false,
  saving,
  onSavingChange,
}: AssignWorkersModalProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(
    workers.filter(w => w.assigned).map(w => w.id)
  )

  // Re-sync selections when workers prop changes (e.g., modal reopened after server update)
  useEffect(() => {
    setSelectedIds(workers.filter(w => w.assigned).map(w => w.id))
  }, [workers])

  const toggleWorker = (id: string) => {
    if (isNotion) {
      setSelectedIds(prev => prev.includes(id) ? [] : [id])
    } else {
      setSelectedIds(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      )
    }
  }

  const handleSave = () => {
    onSavingChange(true)
    const payload: { provider: string; worker_ids: string[]; integration_id?: string } = { provider, worker_ids: selectedIds }
    if (integrationId) payload.integration_id = integrationId

    router.post('/dashboard/integrations/bulk_assign_workers', payload as Record<string, string | string[]>, {
      preserveScroll: true,
      onSuccess: () => onClose(),
      onFinish: () => onSavingChange(false),
    })
  }

  const handleSkip = () => {
    onClose()
  }



  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{`Atribuir ${providerLabel} aos colaboradores`}</DialogTitle>
          <DialogDescription>
            Selecione os agentes que devem ter acesso:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {workers.map((worker) => (
            <label
              key={worker.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent cursor-pointer transition-colors"
            >
              {isNotion ? (
                <input
                  type="radio"
                  name="notion_worker"
                  checked={selectedIds.includes(worker.id)}
                  onChange={() => toggleWorker(worker.id)}
                  className="accent-brand focus:ring-brand"
                />
              ) : (
                <Checkbox
                  checked={selectedIds.includes(worker.id)}
                  onCheckedChange={() => toggleWorker(worker.id)}
                />
              )}
              <Avatar className="size-8 shrink-0 text-[12px]">
                {worker.avatar_url ? <AvatarImage alt="" src={worker.avatar_url} /> : null}
                <AvatarFallback className="bg-agent text-agent-foreground" style={agentHueStyle(worker.id)}>
                  {initialOf(worker.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{worker.name}</p>
                {worker.job_title && (
                  <p className="text-xs text-muted-foreground truncate">{worker.job_title}</p>
                )}
              </div>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleSkip}>
            Pular
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || selectedIds.length === 0}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AssignWorkersModal
